/* eslint-disable camelcase */
import React, { useContext, useMemo } from 'react';
import { useReactiveVar } from '@apollo/client';
import queryString from 'query-string';

import formatPrice from 'app/utils/price';

import { PathSegmentContext } from 'app/views/Main/PathSegment.context';
import { isGlobalLoadingVar, selectedPeriodVar } from 'app/apollo/reaction';
import { MenuDispatchContext, STORED_POSITION } from 'app/containers/contexts/menu.context';

import classNames from 'classnames';
import { PROMOCODE_TYPES_FIXED, PROMOCODE_TYPES_PERCENT } from 'app/const/promo';
import { useRouterState } from 'app/hooks/useRouterState';
import css from './BR1258OpenBasketButton.module.scss';
import { useBasketDataByLocation } from '../../../../../../../components/OpenBasketButton/hooks';
import { CookiesNotification } from '../../../../../../../components/ui/CookiesNotification/CookiesNotification';

import CartIcon from './img/cart.component.svg';

const getCharByDiscountType = ({ discountType }) => {
    if (PROMOCODE_TYPES_FIXED.includes(discountType)) {
        return ' ₽';
    }
    if (PROMOCODE_TYPES_PERCENT.includes(discountType)) {
        return '%';
    }
    return '%';
};

const calculateProgressPercent = (currentPrice, differenceToPromocode) => {
    const requiredAmount = currentPrice + differenceToPromocode;

    if (currentPrice >= requiredAmount) {
        return 100;
    }
    if (currentPrice === 0) {
        return 0;
    }

    const percent = (currentPrice / requiredAmount) * 100;

    return Math.min(100, Math.max(0, percent));
};

//  const {
//             history,
//             menuDispatchContext,
//         } = this.props;
//         const { location: { search } } = window;

//         menuDispatchContext({
//             type: 'scrollTo',
//             payload: { target: STORED_POSITION, value: window.scrollY },
//         });

//         analyticService.push({ eventName: 'Go_to_Cart' });

//         this.setState({
//             animateEnterOverlay: true,
//         }, () => {
//             history.push(`/basket/${search}`);
//         });

function BR1258OpenBasketButton() {
    const isGlobalLoading = useReactiveVar(isGlobalLoadingVar);
    const period = useReactiveVar(selectedPeriodVar);
    const { cart } = useBasketDataByLocation();
    const { segment } = useContext(PathSegmentContext);
    const [routerState] = useRouterState();
    const { history, location } = routerState;

    const menuDispatchContext = useContext(MenuDispatchContext);

    const isDeliveryBasket = useMemo(
        () => window.location.pathname.includes('app/delivery'),
        [],
    );
    const deliveryId = useMemo(
        () => window.location.pathname.split('/').find((e) => Boolean(e) && !Number.isNaN(Number(e))),
        [],
    );

    const price = useMemo(
        () => cart?.totals?.total_discount_price || 0,
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [cart?.totals?.total_discount_price, isGlobalLoading],
    );

    const discountConditions = useMemo(
        () => cart?.discount_conditions || {},
        [cart?.discount_conditions],
    );

    const { differenceBetweenPromocodeAndOrder, discountValue, discountType } = discountConditions;

    const discountChar = getCharByDiscountType({ discountType });

    const flatSection = useMemo(
        () => cart?.sections?.reduce((acc, section) => [...acc, ...section.items], []),
        [cart],
    );

    const originalPrice = useMemo(
        // eslint-disable-next-line no-unsafe-optional-chaining
        () => {
            if (segment === 'delivery') return 0;
            const t_price = cart?.totals?.total_price || 0;
            const d_price = cart?.totals.delivery_price || 0;
            return t_price + d_price;
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [cart?.totals?.total_price, cart?.totals?.delivery_price, isGlobalLoading],
    );

    const hasDiscount = useMemo(
        () => Boolean(originalPrice && originalPrice !== price),
        [price, originalPrice],
    );

    const handleOpenBasket = () => {
        menuDispatchContext({
            type: 'scrollTo',
            payload: { target: STORED_POSITION, value: window.scrollY },
        });

        if (isDeliveryBasket) {
            const searchParams = queryString.parse(location?.search);
            const nextSearch = queryString.stringify(searchParams);
            const nextUrl = `/app/delivery/${deliveryId}/basket/?${nextSearch}`;
            history.push(nextUrl);
            return;
        }
        history.push(`/basket/${location?.search}`);
    };

    const basketButtonRootClasses = classNames({
        [css.openBasketButtonRoot]: true,
        [css.hiddenWithoutSale]: !flatSection?.length && differenceBetweenPromocodeAndOrder,
        [css.hiddenWithSale]: !flatSection?.length && !differenceBetweenPromocodeAndOrder,
    });

    const percent = calculateProgressPercent(price, differenceBetweenPromocodeAndOrder);

    return (
        <div className={basketButtonRootClasses}>
            <CookiesNotification pageClass="promoCheckout" />
            <div
                onClick={handleOpenBasket}
                role="none"
            >
                {differenceBetweenPromocodeAndOrder ? (
                    <div>
                        <div className={`body-xs-medium ${css.openBasketButtonDiscountText}`}>
                            <span>
                                Добавьте
                                {price ? ' ещё ' : ' '}
                                на
                                {' '}
                                {formatPrice(differenceBetweenPromocodeAndOrder)}
                                {' '}
                                ₽
                            </span>
                            <span>→</span>
                            <span>
                                Скидка
                                {' '}
                                {discountValue}
                                {discountChar}
                            </span>
                        </div>
                        <div className={css.openBasketButtonProgressBar}>
                            <div
                                className={css.openBasketButtonProgressFill}
                                style={{ width: `${percent}%` }}
                            />
                        </div>
                    </div>
                ) : null}
                <div className={css.openBasketButton}>
                    <div className={css.openBasketDishesCount}>
                        <CartIcon className={css.openBasketButtonIcon} />
                        <div className={`other-number-l-medium ${css.openBasketButtonDigits}`}>{flatSection?.length}</div>
                    </div>
                    <div className={css.openBasketPricesContentContainer}>
                        <div className={css.openBasketPriceContainer}>
                            <div className={`other-number-l-medium ${css.openBasketButtonDigits}`}>
                                {formatPrice(price)}
                            </div>
                            <div className={`body-l-semibold ${css.openBasketButtonText}`}>₽</div>
                        </div>
                        {hasDiscount && (
                            <div className={css.openBasketCrossedPriceContainer}>
                                <div className={`other-number-xs-medium ${css.openBasketButtonDigits} ${css.white}`}>
                                    {formatPrice(originalPrice)}
                                </div>
                                <div className={`body-xs-semibold ${css.openBasketButtonText} ${css.white}`}>₽</div>
                                <div className={css.crossedLine} />
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}

export default React.memo(BR1258OpenBasketButton);
